import { useEffect } from 'react';
import { NavLink, Outlet, useLocation, useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useProject } from '../hooks/useFirestoreData';
import { useSuggestions } from '../hooks/useSuggestions';
import { Spinner } from '../components/ui/Spinner';
import { cn } from '../lib/utils';
import { countPending } from '../lib/suggestion';
import { useUndoHotkey } from '../hooks/useUndoHotkey';
import { UndoToast } from '../components/common/UndoToast';
import { useUndoStore } from '../lib/undoStack';

export function ProjectLayout() {
  const { projectId } = useParams<{ projectId: string }>();
  const { data: project, loading } = useProject(projectId ?? null);
  const { data: suggestions } = useSuggestions(projectId ?? null);
  const pendingCount = countPending(suggestions);

  // App-level Cmd+Z / Ctrl+Z for the whole project shell.
  useUndoHotkey();
  // The undo history is scoped to a single project — switching projects
  // must not let a stray Cmd+Z replay an edit against the wrong doc set.
  const resetUndo = useUndoStore((s) => s.reset);
  useEffect(() => {
    resetUndo();
    return () => resetUndo();
  }, [projectId, resetUndo]);

  if (!projectId) return null;

  return (
    <div className="flex h-full flex-col">
      <UndoToast />
      <header className="flex items-center gap-2 border-b border-slate-200 bg-white px-3 py-2 sm:gap-4 sm:px-4">
        <Link
          to="/"
          className="flex shrink-0 items-center gap-1.5 text-slate-500 hover:text-slate-800"
          title="プロジェクト一覧へ戻る"
        >
          <ArrowLeft className="h-4 w-4" />
          <img
            src="/migratus_icon.png"
            alt="MIGRATUS"
            className="h-6 w-6 rounded"
          />
        </Link>
        <h1 className="hidden truncate text-sm font-semibold sm:block">
          {loading ? '読み込み中…' : (project?.name ?? 'プロジェクトが見つかりません')}
        </h1>
        {/*
         * On mobile the project name is hidden (above) and the nav
         * flexes to fill the bar with horizontal scroll so all tabs
         * stay reachable without wrapping. `min-w-0` lets the nav
         * shrink below its intrinsic content width so `overflow-x-auto`
         * actually kicks in.
         */}
        <nav
          data-testid="project-tabs"
          className="ml-0 flex min-w-0 flex-1 gap-1 overflow-x-auto whitespace-nowrap text-sm sm:ml-6 sm:flex-none"
        >
          <Tab to={`/projects/${projectId}/dashboard`}>ダッシュボード</Tab>
          <Tab to={`/projects/${projectId}/me`}>自分のタスク</Tab>
          <Tab to={`/projects/${projectId}/dag`} matchPaths={['dag', 'list']}>
            移行作業
          </Tab>
          <Tab to={`/projects/${projectId}/wbs`}>WBS</Tab>
          <Tab to={`/projects/${projectId}/runs`}>フェーズ</Tab>
          <Tab to={`/projects/${projectId}/suggestions`} badge={pendingCount}>
            リンク候補
          </Tab>
          <Tab to={`/projects/${projectId}/settings`}>設定</Tab>
          <Tab to={`/projects/${projectId}/activity`}>アクティビティ</Tab>
        </nav>
      </header>
      <main className="flex-1 overflow-hidden">
        {loading ? (
          <div className="flex h-full items-center justify-center">
            <Spinner />
          </div>
        ) : !project ? (
          <div className="flex h-full items-center justify-center text-sm text-slate-500">
            プロジェクトが見つかりませんでした
          </div>
        ) : (
          <Outlet />
        )}
      </main>
    </div>
  );
}

function Tab({
  to,
  end,
  children,
  badge,
  matchPaths,
}: {
  to: string;
  end?: boolean;
  children: React.ReactNode;
  badge?: number;
  /** Treat the tab as active if the current URL's last segment is in this
   *  list. Used so 移行作業 lights up for both /dag and /list. */
  matchPaths?: string[];
}) {
  const location = useLocation();
  const currentSegment = location.pathname.split('/').filter(Boolean).pop() ?? '';
  const matchedBySibling = matchPaths ? matchPaths.includes(currentSegment) : false;
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'inline-flex items-center gap-1.5 rounded px-3 py-1 transition-colors',
          isActive || matchedBySibling
            ? 'bg-slate-900 text-white'
            : 'text-slate-700 hover:bg-slate-100',
        )
      }
    >
      {({ isActive }) => {
        const active = isActive || matchedBySibling;
        return (
          <>
            <span>{children}</span>
            {badge && badge > 0 ? (
              <span
                className={cn(
                  'inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full px-1 text-[10px] font-semibold tabular-nums',
                  active
                    ? 'bg-white text-slate-900'
                    : 'bg-red-500 text-white',
                )}
                aria-label={`未処理 ${badge} 件`}
              >
                {badge > 99 ? '99+' : badge}
              </span>
            ) : null}
          </>
        );
      }}
    </NavLink>
  );
}
